import { useDebounce } from '@hooks/debounce';
import ItemService from '@utility/services/item';
import { itemsDropdownHook } from '@hooks/item';
import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  cn,
  Image,
  SearchField,
  SearchSuggest,
} from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { ItemTags } from '@page-components/shipment/ItemsCommon';
import { ItemStates } from '@utility/constants/common';

/* The dropdown endpoint's rows; only the fields these pickers read. */
export type ItemDropdownRow = {
  _id: string;
  p_title?: string;
  short_title?: string;
  p_sku?: string;
  p_price?: number;
  p_currency?: string;
  p_images?: { display_image?: string };
  inventory?: number;
  state?: string;
  is_bundle?: boolean;
  is_duplicate_listing?: boolean;
  search_matched_field?: unknown[];
};

type DropdownItem = ItemDropdownRow;

type SuggestRow = { id: string; item: DropdownItem };

const DISCONTINUED_STATES: (string | undefined)[] = [
  ItemStates.inactive,
  ItemStates.discontinued,
  ItemStates.component,
];

function toRows(items: DropdownItem[]): SuggestRow[] {
  return items.map(item => ({ id: String(item._id), item }));
}

function isOutOfStock(item: DropdownItem, allowZeroInventory: boolean) {
  return item.inventory === 0 && !allowZeroInventory;
}

function ResultItem({
  item,
  allowZeroInventory,
}: {
  item: DropdownItem;
  allowZeroInventory: boolean;
}) {
  const outOfStock = isOutOfStock(item, allowZeroInventory);
  const matchedFields = CommonUtility.isValidArray(item.search_matched_field)
    ? item.search_matched_field.filter(
        (field): field is string => typeof field === 'string',
      )
    : [];

  return (
    <div className="sk:flex sk:w-full sk:min-w-0 sk:items-center sk:gap-4 sk:py-1">
      <Image
        src={item.p_images?.display_image}
        alt=""
        size="md"
        className={cn(outOfStock && 'sk:opacity-50')}
      />
      <div
        className={cn(
          'sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-1',
          outOfStock && 'sk:opacity-50',
        )}
      >
        <span className="sk:flex sk:flex-wrap sk:items-center sk:gap-2 sk:text-fg">
          {item.short_title || item.p_title}
          {item.state === ItemStates.component ? (
            <Badge tone="neutral">Component</Badge>
          ) : null}
        </span>
        <span className="sk:font-semibold sk:text-fg">{item.p_sku}</span>
        {matchedFields.length > 0 ? (
          <span className="sk:flex sk:flex-wrap sk:items-center sk:gap-1 sk:text-fg-tertiary">
            Matched Fields
            {matchedFields.map(field => (
              <Badge key={field} tone="info">
                {field}
              </Badge>
            ))}
          </span>
        ) : null}
      </div>
      <span
        className={cn(
          'sk:min-w-12.5 sk:shrink-0 sk:text-fg',
          outOfStock && 'sk:opacity-50',
        )}
      >
        {CommonUtility.currencyFormat(item.p_price, item.p_currency)}
      </span>
      <span className="sk:min-w-26 sk:shrink-0 sk:whitespace-nowrap sk:text-fg-secondary">
        {outOfStock ? 'Out of stock' : null}
      </span>
    </div>
  );
}

type ItemSearchInputProps = {
  regionId?: string;
  /** Opens the results above the field, for a field near the bottom of a dialog. */
  top?: boolean;
  onResultClick: (item: DropdownItem) => void;
  /** Items already chosen, left out of the results. */
  currentItems?: { _id: string }[];
  allowZeroInventory?: boolean;
  /** Leaves focus once a row is chosen instead of filling in its title. */
  removeSearchAfterSelect?: boolean;
  shouldRefreshOnRegionChange?: boolean;
  /** The old list's height; the kit list has its own. */
  resultHeight?: number;
  isInternational?: boolean;
};

/** Search the region's catalog and pick an item; scrolling loads more rows. */
export function ItemSearchInput({
  regionId,
  top = false,
  onResultClick,
  currentItems,
  allowZeroInventory = false,
  removeSearchAfterSelect = false,
  shouldRefreshOnRegionChange = true,
  isInternational = false,
}: ItemSearchInputProps) {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const {
    data: hookData,
    loading: hookLoading,
    nextPage,
  } = itemsDropdownHook(regionId, search, false, isInternational);
  const [data, setData] = useState<DropdownItem[]>([]);
  const [noSearchData, setNoSearchData] = useState<DropdownItem[]>([]);

  useEffect(() => {
    setData([]);
    setLoading(Boolean(search));
  }, [search]);

  useEffect(() => {
    const fetchDefaults = async () => {
      const result = await ItemService.itemDropdown(regionId, '');
      setNoSearchData(result.data);
    };
    if (regionId && shouldRefreshOnRegionChange) {
      fetchDefaults();
    }
  }, [regionId]);

  useEffect(() => {
    setData(hookData);
  }, [hookData]);

  useEffect(() => {
    setLoading(hookLoading);
  }, [hookLoading]);

  const results = useMemo(() => {
    if (search && data.length === 0) {
      return [];
    }
    const source = data.length > 0 ? data : noSearchData;
    if (!currentItems?.length) {
      return source;
    }
    return source.filter(
      item => !currentItems.some(current => current._id === item._id),
    );
  }, [search, data, currentItems, noSearchData]);

  const rows = useMemo(() => toRows(results), [results]);
  const disabledKeys = useMemo(
    () =>
      rows
        .filter(row => isOutOfStock(row.item, allowZeroInventory))
        .map(row => row.id),
    [rows, allowZeroInventory],
  );

  const select = ({ item }: SuggestRow) => {
    onResultClick(item);
    if (removeSearchAfterSelect) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    } else {
      setSearch(item.p_title ?? '');
    }
  };

  return (
    <SearchSuggest
      label="Search items"
      placeholder="Search items"
      value={search}
      onValueChange={setSearch}
      items={rows}
      onSelect={select}
      disabledKeys={disabledKeys}
      isLoading={loading}
      isDisabled={!regionId}
      onLoadMore={nextPage}
      openOnFocus
      isFluid
      placement={top ? 'top' : 'bottom'}
    >
      {row => (
        <ResultItem item={row.item} allowZeroInventory={allowZeroInventory} />
      )}
    </SearchSuggest>
  );
}

type RevenueItemSearchInputProps = {
  search?: string;
  searchChanged?: (value: string) => void;
};

/* Its one caller passes nothing yet, so the field also works uncontrolled. */
export function RevenueItemSearchInput({
  search,
  searchChanged,
}: RevenueItemSearchInputProps) {
  return (
    <SearchField
      aria-label="Search by Item"
      placeholder="Search by Item"
      value={search}
      onChange={searchChanged}
    />
  );
}

function SearchContent({ item }: { item: DropdownItem }) {
  return (
    <div className="sk:flex sk:w-full sk:min-w-0 sk:items-center sk:gap-4 sk:py-1">
      <Image src={item.p_images?.display_image} alt="" size="md" />
      <div className="sk:flex sk:min-w-0 sk:flex-1 sk:flex-col sk:gap-1">
        <span className="sk:text-fg">
          {item.short_title || item.p_title}
          <ItemTags
            sku={item.p_sku}
            isDiscontinued={DISCONTINUED_STATES.includes(item.state)}
            isInactive={item.state === ItemStates.inactive}
            isMissingInfo={item.state === ItemStates.conflict}
            isDuplicateListing={!!item.is_duplicate_listing}
            state={item.state}
          />
        </span>
        <span className="sk:font-semibold sk:text-fg">{item.p_sku}</span>
      </div>
    </div>
  );
}

type ItemSelectSearchProps = {
  regionId?: string;
  onItemChanged?: (item: DropdownItem | undefined) => void;
  excludeIds?: string[];
  optionalRegion?: boolean;
  /** Offers only resolved items. */
  excludeInvalid?: boolean;
};

/** Picks one item by name or SKU; bundles are listed but can't be picked. */
export function ItemSelectSearch({
  regionId,
  onItemChanged,
  excludeIds = [],
  optionalRegion,
  excludeInvalid,
}: ItemSelectSearchProps) {
  const [search, setSearch] = useState('');
  const finalSearch = useDebounce(search, 1000, 1);
  const { data, loading } = itemsDropdownHook(
    regionId,
    finalSearch,
    optionalRegion,
  );

  const rows = useMemo(() => {
    let items: DropdownItem[] = data ?? [];
    if (excludeIds.length > 0) {
      items = items.filter(item => !excludeIds.includes(item._id));
    }
    if (excludeInvalid) {
      items = items.filter(item => item.state === 'Resolved');
    }
    return toRows(items);
  }, [data, excludeIds, excludeInvalid]);

  const disabledKeys = useMemo(
    () => rows.filter(row => row.item.is_bundle).map(row => row.id),
    [rows],
  );

  return (
    <SearchSuggest
      label="Search by Name or SKU"
      placeholder="Search by Name or SKU"
      value={search}
      onValueChange={setSearch}
      items={rows}
      onSelect={({ item }) => {
        setSearch(item.p_title ?? '');
        onItemChanged?.(item);
      }}
      disabledKeys={disabledKeys}
      isLoading={loading || search !== finalSearch}
      isFluid
    >
      {row => <SearchContent item={row.item} />}
    </SearchSuggest>
  );
}
